import React, { useState, useRef, useEffect } from 'react'
import { Modal, Button, message, Slider, Spin, Tooltip } from 'antd'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import { transitions as transitionTypes } from '../../helper/data.video'
import { TransitionModal } from '../Scene/SceneTransition'
import './VideoEditor.scss'

const isUsableClipShot = shot =>
  !!shot?.vidSrc || (!!shot?.is_static && !!shot?.imageSrc)

const DragHandle = SortableHandle(() => (
  <div className="video-editor__drag-handle" />
))

const SortableClip = SortableElement(
  ({ clip, displayIndex, onDelete, onClick, isActive }) => {
    return (
      <div
        className={`video-editor__clip ${
          isActive ? 'video-editor__clip--active' : ''
        }`}
        onClick={e => {
          e.stopPropagation()
          onClick(displayIndex)
        }}
      >
        <DragHandle />

        <img
          src={clip.imageSrc}
          alt={clip.shotTitle}
          className="video-editor__clip-image"
        />

        <div className="video-editor__clip-number">{displayIndex + 1}</div>

        <div className="video-editor__clip-footer">
          <span className="video-editor__clip-title">
            {clip.shotTitle}
            {clip.is_static ? ' (Static)' : ''}
          </span>
          <button
            onClick={e => {
              e.stopPropagation()
              onDelete(displayIndex)
            }}
            className="video-editor__clip-delete"
          >
            ×
          </button>
        </div>
      </div>
    )
  }
)

// Transition marker component between clips
const TransitionMarker = ({ transition, isEnd = false, onClick }) => {
  if (!transition) return null

  // Get the transition type and duration
  const transitionType =
    typeof transition === 'string' ? transition : transition?.transition
  const duration = typeof transition === 'object' ? transition?.duration : 1
  const label = transition?.label || 'Transition'

  // Find the matching transition from our data
  const transitionData = transitionTypes.find(t => t.id === transitionType)
  const TransitionIcon = transitionData?.icon

  const tooltipContent = (
    <div className="video-editor__transition-tooltip">
      <div className="video-editor__transition-tooltip-label">{label}</div>
      <div className="video-editor__transition-tooltip-name">
        {transitionData?.name || transitionType}
      </div>
      <div className="video-editor__transition-tooltip-duration">
        Duration: {duration}s
      </div>
    </div>
  )

  return (
    <Tooltip title={tooltipContent} placement="top">
      <div
        className={`video-editor__transition-marker ${
          isEnd ? 'video-editor__transition-marker--end' : ''
        }`}
        onClick={event => {
          event.stopPropagation()
          if (onClick) onClick(transition)
        }}
      >
        <div className="video-editor__transition-marker-icon">
          {TransitionIcon ? <TransitionIcon size={12} /> : '⬡'}
        </div>
        <div className="video-editor__transition-marker-line" />
      </div>
    </Tooltip>
  )
}

const SortableClipList = SortableContainer(
  ({
    clips,
    onDelete,
    onClick,
    activeIndex,
    timelineRef,
    onTransitionEdit
  }) => (
    <div className="video-editor__timeline-clips" ref={timelineRef}>
      {clips.map((clip, index) => {
        // Separate start/shot transitions from end transitions
        const startTransitions = clip.transitions
          ? clip.transitions.filter(t => !t.isEndTransition)
          : []
        const endTransitions = clip.transitions
          ? clip.transitions.filter(t => t.isEndTransition)
          : []
        const isLastClip = index === clips.length - 1

        return (
          <React.Fragment
            key={`clip-wrapper-${clip.sceneId}-${clip.shotId}-${index}`}
          >
            {/* Show start/shot transition markers before the clip */}
            {startTransitions.length > 0 && (
              <div className="video-editor__transition-group video-editor__transition-group--start">
                {startTransitions.map((trans, tIndex) => (
                  <TransitionMarker
                    key={`trans-start-${index}-${tIndex}`}
                    transition={trans}
                    onClick={onTransitionEdit}
                  />
                ))}
              </div>
            )}
            <SortableClip
              key={`clip-${clip.sceneId}-${clip.shotId}-${index}`}
              index={index}
              displayIndex={index}
              clip={clip}
              onDelete={onDelete}
              onClick={onClick}
              isActive={activeIndex === index}
            />
            {/* Show end transition markers after the clip (only for last clip or scene end) */}
            {endTransitions.length > 0 && (
              <div className="video-editor__transition-group video-editor__transition-group--end">
                {endTransitions.map((trans, tIndex) => (
                  <TransitionMarker
                    key={`trans-end-${index}-${tIndex}`}
                    transition={trans}
                    isEnd={true}
                    onClick={onTransitionEdit}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
)

const VideoEditorModal = ({
  visible,
  scenes,
  transitions,
  onConfirm,
  onAdvancedEditor,
  onCancel,
  finalVideoExists,
  isSingleScene = false
}) => {
  const [clips, setClips] = useState([])
  const [activeClipIndex, setActiveClipIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [localTransitions, setLocalTransitions] = useState({})
  const [transitionEditor, setTransitionEditor] = useState({
    visible: false,
    sceneId: null,
    position: null,
    currentTransition: null
  })

  // Use two video elements and swap between them
  const video1Ref = useRef(null)
  const video2Ref = useRef(null)
  const [activeVideoIndex, setActiveVideoIndex] = useState(0) // 0 or 1
  const isSeekingRef = useRef(false)
  const loadingTimeoutRef = useRef(null)
  const isAutoPlayingRef = useRef(false)
  const staticPlaybackIntervalRef = useRef(null)
  const staticPlaybackStartRef = useRef(null)

  // Audio management
  const audioRef = useRef(null)
  const timelineRef = useRef(null)
  const [currentSceneId, setCurrentSceneId] = useState(null)
  const [sceneStartClipIndex, setSceneStartClipIndex] = useState(0)
  const audioSyncTimeoutRef = useRef(null)

  // Get current active video ref
  const getActiveVideoRef = () => {
    return activeVideoIndex === 0 ? video1Ref : video2Ref
  }

  // Get next (preload) video ref
  const getPreloadVideoRef = () => {
    return activeVideoIndex === 0 ? video2Ref : video1Ref
  }

  const stopStaticPlaybackTimer = () => {
    if (staticPlaybackIntervalRef.current) {
      clearInterval(staticPlaybackIntervalRef.current)
      staticPlaybackIntervalRef.current = null
    }
    staticPlaybackStartRef.current = null
  }

  const getClipDuration = clip => {
    const parsed = parseFloat(
      clip?.video_duration || clip?.generated_video_duration || 0
    )
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }

  const getSceneAudioOffset = (clipIndex, clipTime = 0) => {
    const activeClip = clips[clipIndex]
    if (!activeClip) return 0

    return clips.reduce((offset, clip, index) => {
      if (index >= clipIndex || clip.sceneId !== activeClip.sceneId) {
        return offset
      }
      return offset + getClipDuration(clip)
    }, clipTime)
  }

  const syncAudioForClip = (
    clipIndex,
    clipTime = 0,
    shouldPlay = isPlaying,
    forceSeek = false
  ) => {
    const audio = audioRef.current
    const activeClip = clips[clipIndex]
    if (!audio || !activeClip) return

    if (!activeClip.audio_link) {
      audio.pause()
      audio.removeAttribute('src')
      return
    }

    const nextAudioSrc = activeClip.audio_link
    const nextAudioTime = getSceneAudioOffset(clipIndex, clipTime)
    const applyAudioTime = () => {
      // Avoid micro-seeks during autoplay shot transitions in the same scene.
      // Frequent tiny seeks can produce audible "jumps"/clicks.
      const drift = Math.abs((audio.currentTime || 0) - nextAudioTime)
      if (forceSeek || drift > 0.2) {
        try {
          audio.currentTime = nextAudioTime
        } catch (err) {
          console.error('Error seeking scene audio:', err)
        }
      }

      if (shouldPlay) {
        audio.play().catch(err => console.error('Error playing audio:', err))
      }
    }

    if (audio.getAttribute('src') !== nextAudioSrc) {
      audio.pause()
      audio.src = nextAudioSrc
      audio.load()
      audio.onloadedmetadata = applyAudioTime
      return
    }

    applyAudioTime()
  }

  const handleClipEnd = currentIndex => {
    const audio = audioRef.current

    if (isPlaying && currentIndex < clips.length - 1) {
      const nextClip = clips[currentIndex + 1]
      const currentClip = clips[currentIndex]
      const isSameScene = nextClip.sceneId === currentClip.sceneId

      if (!isSameScene && audio) {
        audio.pause()
      }

      setCurrentTime(0)
      setActiveClipIndex(currentIndex + 1)
      return
    }

    setIsPlaying(false)
    if (currentIndex === clips.length - 1) {
      setCurrentTime(0)
      setActiveClipIndex(0)
      if (audio) {
        audio.pause()
        audio.currentTime = 0
      }
    }
  }

  useEffect(() => {
    if (visible) {
      setLocalTransitions(transitions || {})
    }
  }, [visible, transitions])

  useEffect(() => {
    if (visible && scenes) {
      const allClips = []
      scenes.forEach(scene => {
        const validShots = scene.shots.filter(isUsableClipShot)

        validShots.forEach((shot, shotIndex) => {
          const shotTransitionKey = `${scene.id}-${shot.id}`
          const sceneStartKey = `${scene.id}-start`
          const sceneEndKey = `${scene.id}-end`

          // Collect all transitions for this clip
          const clipTransitions = []

          // Check for scene START transition (only for first shot of the scene)
          if (shotIndex === 0 && localTransitions[sceneStartKey]) {
            clipTransitions.push({
              ...localTransitions[sceneStartKey],
              label: 'Scene Start',
              sceneId: scene.id,
              position: 'start'
            })
          }

          // Check for shot-level transition - MOVE TO END per user request
          if (localTransitions[shotTransitionKey]) {
            clipTransitions.push({
              ...localTransitions[shotTransitionKey],
              label: 'Shot Transition',
              isEndTransition: true, // User requested shot transitions to be at the end/outro
              sceneId: scene.id,
              position: shot.id
            })
          }

          // Check for scene END transition (only for last shot of the scene)
          if (
            shotIndex === validShots.length - 1 &&
            localTransitions[sceneEndKey]
          ) {
            clipTransitions.push({
              ...localTransitions[sceneEndKey],
              label: 'Scene End',
              isEndTransition: true,
              sceneId: scene.id,
              position: 'end'
            })
          }

          allClips.push({
            sceneId: scene.id,
            shotId: shot.id,
            sceneTitle: scene.title,
            shotTitle: shot.title,
            vidSrc: shot.vidSrc,
            imageSrc: shot.imageSrc,
            is_static: !!shot.is_static,
            video_duration: shot.video_duration,
            generated_video_duration: shot.generated_video_duration,
            // Store all transitions for this clip
            transitions: clipTransitions.length > 0 ? clipTransitions : null,
            // Keep backward compatibility
            transition: clipTransitions[0] || null,
            audio_link: scene.audio_link
          })
        })
      })
      setClips(prevClips => {
        if (!prevClips || prevClips.length === 0) return allClips

        // Preserve current clip order when transitions change.
        const nextByKey = new Map(
          allClips.map(clip => [`${clip.sceneId}::${clip.shotId}`, clip])
        )
        const ordered = []

        prevClips.forEach(clip => {
          const key = `${clip.sceneId}::${clip.shotId}`
          if (nextByKey.has(key)) {
            ordered.push(nextByKey.get(key))
            nextByKey.delete(key)
          }
        })

        nextByKey.forEach(clip => ordered.push(clip))
        return ordered
      })

      setActiveClipIndex(prevIndex => {
        if (allClips.length === 0) return 0
        return prevIndex >= allClips.length ? allClips.length - 1 : prevIndex
      })
    }
  }, [visible, scenes, localTransitions])

  const handleTransitionEdit = transitionItem => {
    if (!transitionItem?.sceneId || !transitionItem?.position) return
    setTransitionEditor({
      visible: true,
      sceneId: transitionItem.sceneId,
      position: transitionItem.position,
      currentTransition: {
        transition: transitionItem.transition,
        duration: transitionItem.duration || 1
      }
    })
  }

  const handleTransitionConfirm = transitionData => {
    const { sceneId, position } = transitionEditor
    if (!sceneId || !position) return
    const key = `${sceneId}-${position}`

    setLocalTransitions(prev => {
      const next = { ...prev }
      if (!transitionData) {
        delete next[key]
      } else {
        next[key] = transitionData
      }
      return next
    })

    setTransitionEditor({
      visible: false,
      sceneId: null,
      position: null,
      currentTransition: null
    })
  }

  const handleApplyTransitionToAll = transitionData => {
    if (!transitionData) return
    setLocalTransitions(() => {
      const allTransitions = {}
      const firstSceneId = scenes?.[0]?.id

      ;(scenes || []).forEach(scene => {
        if (!scene?.id) return
        const validShots = (scene.shots || []).filter(isUsableClipShot)
        if (validShots.length === 0) return

        if (scene.id === firstSceneId) {
          allTransitions[`${scene.id}-start`] = transitionData
        }

        validShots.forEach((shot, index) => {
          const position = index === validShots.length - 1 ? 'end' : shot.id
          allTransitions[`${scene.id}-${position}`] = transitionData
        })
      })

      return allTransitions
    })
  }

  const handleRemoveAllTransitions = () => {
    setLocalTransitions({})
  }

  // Sync audio with video playback
  useEffect(() => {
    const activeVideo = getActiveVideoRef().current
    const audio = audioRef.current

    if (!activeVideo || !audio || !clips[activeClipIndex]) return

    const handleVideoPlay = () => {
      if (audio.src && !audio.ended) {
        audio.play().catch(err => console.error('Error playing audio:', err))
      }
    }

    const handleVideoPause = () => {
      if (audio.src) {
        audio.pause()
      }
    }

    const handleVideoWaiting = () => {
      if (audio.src) {
        audio.pause()
      }
    }

    const handleVideoPlaying = () => {
      if (audio.src && !audio.ended && isPlaying) {
        audio.play().catch(err => console.error('Error playing audio:', err))
      }
    }

    activeVideo.addEventListener('play', handleVideoPlay)
    activeVideo.addEventListener('pause', handleVideoPause)
    activeVideo.addEventListener('waiting', handleVideoWaiting)
    activeVideo.addEventListener('playing', handleVideoPlaying)

    return () => {
      activeVideo.removeEventListener('play', handleVideoPlay)
      activeVideo.removeEventListener('pause', handleVideoPause)
      activeVideo.removeEventListener('waiting', handleVideoWaiting)
      activeVideo.removeEventListener('playing', handleVideoPlaying)
    }
  }, [activeClipIndex, clips, isPlaying, activeVideoIndex])

  // Keep scene audio aligned to the active clip's offset within that scene.
  useEffect(() => {
    const currentClip = clips[activeClipIndex]
    if (!currentClip) return

    setCurrentSceneId(currentClip.sceneId)
    setSceneStartClipIndex(
      clips.findIndex(clip => clip.sceneId === currentClip.sceneId)
    )

    syncAudioForClip(activeClipIndex, 0, isPlaying, false)

    return () => {
      const audio = audioRef.current
      if (audio) {
        audio.onloadedmetadata = null
      }
    }
  }, [activeClipIndex, clips])

  useEffect(() => {
    const activeVideo = getActiveVideoRef().current
    const preloadVideo = getPreloadVideoRef().current

    if (!activeVideo || !clips[activeClipIndex]) return

    const activeClip = clips[activeClipIndex]

    if (activeClip.is_static) {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      activeVideo.pause()
      activeVideo.removeAttribute('src')
      activeVideo.load()
      setDuration(getClipDuration(activeClip))
      setCurrentTime(0)
      setIsLoading(false)
      return
    }

    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
    }

    // Only show loading after a delay (300ms) - this prevents flicker on quick loads
    // Skip loading indicator during autoplay transitions since video is preloaded
    if (!isAutoPlayingRef.current) {
      loadingTimeoutRef.current = setTimeout(() => {
        setIsLoading(true)
      }, 300)
    }

    // Load current clip in active video
    if (activeVideo.src !== activeClip.vidSrc) {
      activeVideo.src = activeClip.vidSrc
      activeVideo.load()
    } else {
      // Video already loaded, clear loading immediately
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      setIsLoading(false)
    }

    // Preload next clip in the other video element
    if (preloadVideo && activeClipIndex < clips.length - 1) {
      const nextClip = clips[activeClipIndex + 1]
      if (
        nextClip &&
        !nextClip.is_static &&
        preloadVideo.src !== nextClip.vidSrc
      ) {
        preloadVideo.src = nextClip.vidSrc
        preloadVideo.load()
      }
    }

    const handleLoadedMetadata = () => {
      setDuration(activeVideo.duration || 0)
      setCurrentTime(0)

      // Clear loading timeout and hide loader
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      setIsLoading(false)

      if (isPlaying) {
        const playPromise = activeVideo.play()
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.error('Error playing video:', err)
            setIsPlaying(false)
          })
        }
      }
    }

    const handleTimeUpdate = () => {
      if (!isSeekingRef.current) {
        setCurrentTime(activeVideo.currentTime || 0)
      }
    }

    const handleEnded = () => {
      // Mark as autoplay transition
      isAutoPlayingRef.current = true
      // Swap to preloaded video for instant playback
      setActiveVideoIndex(prev => (prev === 0 ? 1 : 0))
      handleClipEnd(activeClipIndex)
      // Reset autoplay flag after transition
      setTimeout(() => {
        isAutoPlayingRef.current = false
      }, 100)
    }

    const handleSeeking = () => {
      isSeekingRef.current = true
    }

    const handleSeeked = () => {
      isSeekingRef.current = false
    }

    const handleCanPlay = () => {
      // Clear loading timeout and hide loader when video can play
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      setIsLoading(false)
    }

    const handleWaiting = () => {
      // Only show loading if we're actually waiting (buffering)
      if (!isAutoPlayingRef.current) {
        loadingTimeoutRef.current = setTimeout(() => {
          setIsLoading(true)
        }, 300)
      }
    }

    activeVideo.addEventListener('loadedmetadata', handleLoadedMetadata)
    activeVideo.addEventListener('timeupdate', handleTimeUpdate)
    activeVideo.addEventListener('ended', handleEnded)
    activeVideo.addEventListener('seeking', handleSeeking)
    activeVideo.addEventListener('seeked', handleSeeked)
    activeVideo.addEventListener('canplay', handleCanPlay)
    activeVideo.addEventListener('waiting', handleWaiting)

    return () => {
      // Clear timeout on cleanup
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      activeVideo.removeEventListener('loadedmetadata', handleLoadedMetadata)
      activeVideo.removeEventListener('timeupdate', handleTimeUpdate)
      activeVideo.removeEventListener('ended', handleEnded)
      activeVideo.removeEventListener('seeking', handleSeeking)
      activeVideo.removeEventListener('seeked', handleSeeked)
      activeVideo.removeEventListener('canplay', handleCanPlay)
      activeVideo.removeEventListener('waiting', handleWaiting)
    }
  }, [activeClipIndex, clips, isPlaying, activeVideoIndex])

  // Static clip playback: progress is timer-driven while scene audio keeps playing.
  useEffect(() => {
    stopStaticPlaybackTimer()

    const activeClip = clips[activeClipIndex]
    if (!activeClip || !activeClip.is_static || !isPlaying) return

    const clipDuration = getClipDuration(activeClip)
    if (clipDuration <= 0) {
      handleClipEnd(activeClipIndex)
      return
    }

    if (!staticPlaybackStartRef.current) {
      staticPlaybackStartRef.current = Date.now() - currentTime * 1000
    }

    staticPlaybackIntervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - staticPlaybackStartRef.current) / 1000
      const nextTime = Math.min(elapsed, clipDuration)
      setCurrentTime(nextTime)

      if (elapsed >= clipDuration) {
        stopStaticPlaybackTimer()
        handleClipEnd(activeClipIndex)
      }
    }, 100)

    return () => stopStaticPlaybackTimer()
  }, [activeClipIndex, clips, isPlaying])

  useEffect(() => {
    return () => stopStaticPlaybackTimer()
  }, [])

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return

    const newClips = [...clips]
    const [removed] = newClips.splice(oldIndex, 1)
    newClips.splice(newIndex, 0, removed)
    setClips(newClips)

    if (activeClipIndex === oldIndex) {
      setActiveClipIndex(newIndex)
    } else if (oldIndex < activeClipIndex && newIndex >= activeClipIndex) {
      setActiveClipIndex(prev => prev - 1)
    } else if (oldIndex > activeClipIndex && newIndex <= activeClipIndex) {
      setActiveClipIndex(prev => prev + 1)
    }

    message.success('Clip reordered')
  }

  // Auto-scroll timeline when active clip changes
  useEffect(() => {
    if (timelineRef.current && activeClipIndex !== undefined) {
      const activeElement = timelineRef.current.children[activeClipIndex]
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }
  }, [activeClipIndex])

  const handleDelete = index => {
    Modal.confirm({
      title: 'Delete clip',
      content: 'Remove this clip from the final video?',
      centered: true,
      okText: 'Delete',
      okType: 'danger',
      onOk() {
        const newClips = clips.filter((_, i) => i !== index)
        setClips(newClips)

        if (newClips.length === 0) {
          setActiveClipIndex(0)
          setIsPlaying(false)
        } else if (activeClipIndex >= newClips.length) {
          setActiveClipIndex(newClips.length - 1)
        } else if (activeClipIndex === index && isPlaying) {
          setIsPlaying(false)
        }

        message.success('Clip deleted')
      }
    })
  }

  const handlePlayPause = () => {
    const video = getActiveVideoRef().current
    const audio = audioRef.current

    if (clips[activeClipIndex]?.is_static) {
      if (isPlaying) {
        stopStaticPlaybackTimer()
        if (audio && audio.src) {
          audio.pause()
        }
        setIsPlaying(false)
        return
      }

      staticPlaybackStartRef.current = Date.now() - currentTime * 1000
      setIsPlaying(true)
      if (audio && audio.src && !audio.ended) {
        audio.play().catch(err => console.error('Error playing audio:', err))
      }
      return
    }

    if (!video) return

    if (isPlaying) {
      video.pause()
      if (audio && audio.src) {
        audio.pause()
      }
      setIsPlaying(false)
    } else {
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true)
            if (audio && audio.src && !audio.ended) {
              audio
                .play()
                .catch(err => console.error('Error playing audio:', err))
            }
          })
          .catch(err => {
            console.error('Error playing video:', err)
            message.error('Failed to play video')
          })
      }
    }
  }

  const handlePrevious = () => {
    if (activeClipIndex > 0) {
      const audio = audioRef.current
      stopStaticPlaybackTimer()
      setCurrentTime(0)
      setActiveClipIndex(prev => prev - 1)
      setIsPlaying(false)

      // Pause audio when manually changing clips
      if (audio && audio.src) {
        audio.pause()
      }
    }
  }

  const handleNext = () => {
    if (activeClipIndex < clips.length - 1) {
      const audio = audioRef.current
      stopStaticPlaybackTimer()

      // If next video is preloaded, swap to it for instant playback
      const preloadVideo = getPreloadVideoRef().current
      if (
        preloadVideo &&
        preloadVideo.src === clips[activeClipIndex + 1].vidSrc
      ) {
        setActiveVideoIndex(prev => (prev === 0 ? 1 : 0))
      }
      setCurrentTime(0)
      setActiveClipIndex(prev => prev + 1)
      setIsPlaying(false)

      // Pause audio when manually changing clips
      if (audio && audio.src) {
        audio.pause()
      }
    }
  }

  const handleSeek = value => {
    const video = getActiveVideoRef().current
    const audio = audioRef.current
    if (!isNaN(value) && clips[activeClipIndex]?.is_static) {
      setCurrentTime(value)
      staticPlaybackStartRef.current = Date.now() - value * 1000
      syncAudioForClip(activeClipIndex, value, isPlaying, true)
      return
    }
    if (video && !isNaN(value)) {
      video.currentTime = value
      setCurrentTime(value)
      syncAudioForClip(activeClipIndex, value, isPlaying, true)
    }
  }

  const handleClipClick = index => {
    const audio = audioRef.current
    stopStaticPlaybackTimer()
    // Stop playing when switching clips manually
    setIsPlaying(false)
    // Pause audio
    if (audio && audio.src) {
      audio.pause()
    }
    // Switch active clip
    setCurrentTime(0)
    setActiveClipIndex(index)
  }

  const handleConfirm = () => {
    if (clips.length === 0) {
      message.error('You need at least one clip to generate the final video')
      return
    }

    if (finalVideoExists) {
      Modal.confirm({
        title: 'Overwrite Final Video',
        content:
          'A final video already exists. Generating a new one will overwrite the existing video. Do you want to continue?',
        centered: true,
        okText: 'Yes, Overwrite',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          onConfirm(clips)
        }
      })
    } else {
      Modal.confirm({
        title: 'Generate Final Video',
        content: 'Are you sure you want to generate the final video?',
        centered: true,
        okText: 'Yes, Generate',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          onConfirm(clips)
        }
      })
    }
  }

  const handleAdvancedEditor = () => {
    if (clips.length === 0) {
      message.error('You need at least one clip to open the timeline editor')
      return
    }

    if (onAdvancedEditor) {
      onAdvancedEditor(clips)
    }
  }

  const formatTime = seconds => {
    if (isNaN(seconds) || !isFinite(seconds)) {
      return '00:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  return (
    <Modal
      visible={visible}
      title={null}
      width="95%"
      style={{ top: 20, maxWidth: '1400px' }}
      centered
      onCancel={onCancel}
      footer={null}
      bodyStyle={{ padding: 0, background: '#141414' }}
      destroyOnClose
      getContainer={false}
      closable={false}
    >
      <div className="video-editor">
        {/* Header */}
        <div className="video-editor__header">
          <div>
            <h3 className="video-editor__title">Video Preview</h3>
            <div className="video-editor__subtitle">
              {clips.length} clip{clips.length !== 1 ? 's' : ''} in timeline
            </div>
          </div>
          <div className="video-editor__header-actions">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              icon="tool"
              onClick={handleAdvancedEditor}
              disabled={clips.length === 0}
            >
              Timeline Editor
            </Button>
            {!isSingleScene && (
              <Button
                type="primary"
                icon="play-circle"
                onClick={handleConfirm}
                disabled={clips.length === 0}
              >
                Generate Final Video
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="video-editor__content">
          {/* Video Preview */}
          <div className="video-editor__preview">
            {clips.length > 0 ? (
              <>
                {clips[activeClipIndex]?.is_static && (
                  <img
                    src={clips[activeClipIndex]?.imageSrc}
                    alt={clips[activeClipIndex]?.shotTitle || 'Static clip preview'}
                    className="video-editor__video"
                    style={{ objectFit: 'contain' }}
                  />
                )}
                {/* Loading Spinner */}
                {isLoading && (
                  <div className="video-editor__loading">
                    <Spin size="large" />
                  </div>
                )}

                {/* Video 1 */}
                <video
                  ref={video1Ref}
                  className="video-editor__video"
                  style={{
                    display:
                      activeVideoIndex === 0 &&
                      !clips[activeClipIndex]?.is_static
                        ? 'block'
                        : 'none'
                  }}
                  preload="auto"
                />

                {/* Video 2 - for seamless switching */}
                <video
                  ref={video2Ref}
                  className="video-editor__video"
                  style={{
                    display:
                      activeVideoIndex === 1 &&
                      !clips[activeClipIndex]?.is_static
                        ? 'block'
                        : 'none'
                  }}
                  preload="auto"
                />

                {/* Hidden audio element for scene audio */}
                <audio ref={audioRef} preload="auto" />

                {/* Clip Info Overlay */}
                <div className="video-editor__clip-info">
                  <div className="video-editor__clip-info-title">
                    Clip {activeClipIndex + 1} of {clips.length}
                  </div>
                  <div className="video-editor__clip-info-subtitle">
                    {clips[activeClipIndex]?.sceneTitle} -{' '}
                    {clips[activeClipIndex]?.shotTitle}
                  </div>
                </div>

                {/* Playing Indicator */}
                {isPlaying && (
                  <div className="video-editor__playing-indicator">
                    <span>▶</span> Playing
                  </div>
                )}
              </>
            ) : (
              <div className="video-editor__empty">
                <div className="video-editor__empty-icon">📹</div>
                <div>No clips available</div>
                <div className="video-editor__empty-subtitle">
                  Generate videos first
                </div>
              </div>
            )}
          </div>

          {/* Video Controls */}
          {clips.length > 0 && (
            <div className="video-editor__controls">
              {/* Timeline Slider */}
              <div className="video-editor__slider">
                <Slider
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  onChange={handleSeek}
                  step={0.1}
                  tooltipVisible={false}
                  trackStyle={{ background: '#1890ff' }}
                  handleStyle={{ borderColor: '#1890ff' }}
                />
              </div>

              {/* Playback Controls */}
              <div className="video-editor__playback">
                <div className="video-editor__playback-buttons">
                  <Button
                    icon="step-backward"
                    onClick={handlePrevious}
                    disabled={activeClipIndex === 0}
                    className="video-editor__control-btn"
                  />
                  <Button
                    icon={isPlaying ? 'pause' : 'caret-right'}
                    onClick={handlePlayPause}
                    type="primary"
                    className="video-editor__control-btn video-editor__control-btn--play"
                    style={{ width: '48px' }}
                  />
                  <Button
                    icon="step-forward"
                    onClick={handleNext}
                    disabled={activeClipIndex === clips.length - 1}
                    className="video-editor__control-btn"
                  />

                  <span className="video-editor__time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="video-editor__timeline">
            <div className="video-editor__timeline-header">
              <span className="video-editor__timeline-title">Timeline</span>
              <span className="video-editor__timeline-help">
                Drag clips to reorder • Click to select • Click × to delete
              </span>
            </div>

            <SortableClipList
              clips={clips}
              onSortEnd={handleSortEnd}
              onDelete={handleDelete}
              onClick={handleClipClick}
              onTransitionEdit={handleTransitionEdit}
              activeIndex={activeClipIndex}
              timelineRef={timelineRef}
              axis="x"
              lockAxis="x"
              useDragHandle
              helperClass="sortable-helper"
            />
          </div>
        </div>
      </div>

      <TransitionModal
        visible={transitionEditor.visible}
        onClose={() =>
          setTransitionEditor({
            visible: false,
            sceneId: null,
            position: null,
            currentTransition: null
          })
        }
        onConfirm={handleTransitionConfirm}
        currentTransition={transitionEditor.currentTransition}
        onApplyToAll={handleApplyTransitionToAll}
        onRemoveFromAll={handleRemoveAllTransitions}
      />
    </Modal>
  )
}

export default VideoEditorModal
